import React, { useEffect, useRef, useState } from 'react';
import './TerminalPreview.css';

interface OrbitSDK {
  music: {
    play: () => void;
    pause: () => void;
    toggle: () => void;
    next: () => void;
    prev: () => void;
    getCurrentTrack: () => { name: string; index: number };
    isPlaying: () => boolean;
    playTrack: (index: number) => void;
  };
  apps: {
    openMusicPlayer: () => void;
    openPhotoBooth: () => void;
    openGarden: () => void;
    openGuestbook: () => void;
    openOrbitTV: () => void;
    openGenius: () => void;
    openFileBrowser: (folder?: string) => void;
  };
  user: {
    handle: string | null;
    isLoggedIn: boolean;
  };
  notify: (message: string) => void;
  storage: {
    get: (key: string) => any;
    set: (key: string, value: any) => void;
    clear: () => void;
  };
}

interface TerminalPreviewProps {
  code: string;
  css: string;
  appName: string;
  appEmoji: string;
  orbitSDK: OrbitSDK;
  onError?: (error: string) => void;
}

// Real sandbox: no allow-same-origin (apps run on an opaque origin and cannot
// touch inorbit.fm cookies/storage/DOM). Downloads, popups, forms, modals and
// pointer lock are allowed so apps behave like normal pages.
const SANDBOX_PERMS = 'allow-scripts allow-downloads allow-popups allow-forms allow-modals allow-pointer-lock';

const TerminalPreview: React.FC<TerminalPreviewProps> = ({
  code,
  css,
  appName,
  appEmoji,
  orbitSDK,
  onError
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!iframeRef.current) return;

    setIsLoading(true);
    setError(null);

    // Encode code and CSS as base64 to avoid escaping issues with template literals
    const encodedCode = btoa(unescape(encodeURIComponent(code)));
    const encodedCss = btoa(unescape(encodeURIComponent(css)));
    const storagePrefix = 'orbit_app_' + appName.replace(/[^a-zA-Z0-9]/g, '_') + '_';

    // The sandboxed iframe has no localStorage (opaque origin), so the parent
    // owns storage: seed a snapshot in, apply writes via postMessage.
    const storeSnapshot: Record<string, any> = {};
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.indexOf(storagePrefix) === 0) {
          try {
            storeSnapshot[key.slice(storagePrefix.length)] = JSON.parse(localStorage.getItem(key) || 'null');
          } catch (e) { /* skip unparseable */ }
        }
      }
    } catch (e) { /* localStorage unavailable */ }
    const encodedStore = btoa(unescape(encodeURIComponent(JSON.stringify(storeSnapshot))));

    // Create the HTML content for the iframe
    const htmlContent = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <script src="https://unpkg.com/react@18/umd/react.development.js" crossorigin><` + `/script>
  <script src="https://unpkg.com/react-dom@18/umd/react-dom.development.js" crossorigin><` + `/script>
  <script src="https://unpkg.com/@babel/standalone@7.26.4/babel.min.js"><` + `/script>
  <style id="base-styles">
    * { box-sizing: border-box; }
    body {
      margin: 0;
      padding: 0;
      font-family: 'Geneva', 'Chicago', 'Helvetica Neue', sans-serif;
      background: #fff;
      overflow: auto;
    }
    #root {
      width: 100%;
      height: 100%;
      min-height: 100vh;
    }
  </style>
  <style id="user-styles"></style>
</head>
<body>
  <div id="root"></div>
  <script id="app-data" type="text/plain" data-code="${encodedCode}" data-css="${encodedCss}" data-store="${encodedStore}"><` + `/script>
  <script>
    // Decode and apply user CSS
    (function() {
      var dataEl = document.getElementById('app-data');
      var cssEncoded = dataEl.getAttribute('data-css');
      var userCss = decodeURIComponent(escape(atob(cssEncoded)));
      document.getElementById('user-styles').textContent = userCss;
    })();

    // Error handling
    window.onerror = function(msg, url, lineNo, columnNo, error) {
      window.parent.postMessage({ type: 'error', message: msg }, '*');
      document.getElementById('root').innerHTML = '<div style="color: red; padding: 20px; font-family: monospace;">' + msg + '</div>';
      return false;
    };

    // Orbit SDK setup
    window.orbit = {
      music: {
        getCurrentTrack: function() { return null; },
        isPlaying: function() { return false; }
      },
      user: ${JSON.stringify(orbitSDK.user)}
    };

    // SDK methods that need to communicate with parent
    window.orbit.music.play = function() { window.parent.postMessage({ type: 'sdk', action: 'music.play' }, '*'); };
    window.orbit.music.pause = function() { window.parent.postMessage({ type: 'sdk', action: 'music.pause' }, '*'); };
    window.orbit.music.toggle = function() { window.parent.postMessage({ type: 'sdk', action: 'music.toggle' }, '*'); };
    window.orbit.music.next = function() { window.parent.postMessage({ type: 'sdk', action: 'music.next' }, '*'); };
    window.orbit.music.prev = function() { window.parent.postMessage({ type: 'sdk', action: 'music.prev' }, '*'); };
    window.orbit.music.playTrack = function(index) { window.parent.postMessage({ type: 'sdk', action: 'music.playTrack', data: index }, '*'); };

    window.orbit.apps = {
      openMusicPlayer: function() { window.parent.postMessage({ type: 'sdk', action: 'apps.openMusicPlayer' }, '*'); },
      openPhotoBooth: function() { window.parent.postMessage({ type: 'sdk', action: 'apps.openPhotoBooth' }, '*'); },
      openGarden: function() { window.parent.postMessage({ type: 'sdk', action: 'apps.openGarden' }, '*'); },
      openGuestbook: function() { window.parent.postMessage({ type: 'sdk', action: 'apps.openGuestbook' }, '*'); },
      openOrbitTV: function() { window.parent.postMessage({ type: 'sdk', action: 'apps.openOrbitTV' }, '*'); },
      openGenius: function() { window.parent.postMessage({ type: 'sdk', action: 'apps.openGenius' }, '*'); },
      openFileBrowser: function(folder) { window.parent.postMessage({ type: 'sdk', action: 'apps.openFileBrowser', data: folder }, '*'); }
    };

    window.orbit.notify = function(message) { window.parent.postMessage({ type: 'sdk', action: 'notify', data: message }, '*'); };

    // Per-app storage: in-iframe cache seeded from the parent's localStorage,
    // writes forwarded to the parent (the sandboxed origin has no localStorage).
    (function() {
      var storeData = {};
      try {
        storeData = JSON.parse(decodeURIComponent(escape(atob(document.getElementById('app-data').getAttribute('data-store'))))) || {};
      } catch (e) { storeData = {}; }
      window.orbit.storage = {
        get: function(key) {
          return Object.prototype.hasOwnProperty.call(storeData, key) ? storeData[key] : null;
        },
        set: function(key, value) {
          storeData[key] = value;
          window.parent.postMessage({ type: 'sdk', action: 'storage.set', data: { key: key, value: value } }, '*');
        },
        clear: function() {
          storeData = {};
          window.parent.postMessage({ type: 'sdk', action: 'storage.clear' }, '*');
        }
      };
    })();

    // Decode and compile user code with Babel
    (function() {
      var dataEl = document.getElementById('app-data');
      var codeEncoded = dataEl.getAttribute('data-code');
      var userCode = decodeURIComponent(escape(atob(codeEncoded)));

      // Strip import statements - React is loaded via CDN
      userCode = userCode.replace(/^\\s*import\\s+.*?from\\s+['"].*?['"];?\\s*$/gm, '');
      userCode = userCode.replace(/^\\s*import\\s+['"].*?['"];?\\s*$/gm, '');
      // Strip export statements - not needed for eval
      userCode = userCode.replace(/^\\s*export\\s+default\\s+/gm, '');
      userCode = userCode.replace(/^\\s*export\\s+/gm, '');

      // Prepend React hooks destructuring so they're available in scope
      var hooksPreamble = 'const { useState, useEffect, useRef, useCallback, useMemo, useContext, useReducer, useLayoutEffect, useId, useDeferredValue, useTransition, useSyncExternalStore, useInsertionEffect } = React;\\n' +
        'const { createContext, forwardRef, memo, lazy, Suspense, Fragment, createElement, cloneElement, isValidElement, Children } = React;\\n\\n';

      // Add render call to user code
      var fullCode = hooksPreamble + userCode + '\\n\\n' +
        'try {' +
        '  var root = ReactDOM.createRoot(document.getElementById("root"));' +
        '  root.render(React.createElement(App, { orbit: window.orbit }));' +
        '  window.parent.postMessage({ type: "ready" }, "*");' +
        '} catch (err) {' +
        '  window.parent.postMessage({ type: "error", message: err.message }, "*");' +
        '  document.getElementById("root").innerHTML = "<div style=\\'color: red; padding: 20px; font-family: monospace;\\'>" + err.message + "</div>";' +
        '}';

      try {
        var compiled = Babel.transform(fullCode, { presets: [['react', { runtime: 'classic' }]], sourceType: 'script' }).code;
        eval(compiled);
      } catch(e) {
        console.error('Compilation error:', e);
        window.parent.postMessage({ type: 'error', message: e.message }, '*');
        document.getElementById('root').innerHTML = '<div style="color: red; padding: 20px; font-family: monospace;"><strong>Error:</strong> ' + e.message + '</div>';
      }
    })();
  <` + `/script>
</body>
</html>`;

    // Load via srcdoc (sandboxed without allow-same-origin, contentDocument is inaccessible)
    const iframe = iframeRef.current;
    iframe.setAttribute('srcdoc', htmlContent);

    // Listen for messages from iframe
    const handleMessage = (event: MessageEvent) => {
      if (event.source !== iframe.contentWindow) return;

      const { type, action, data, message } = event.data;

      switch (type) {
        case 'ready':
          setIsLoading(false);
          break;
        case 'error':
          setError(message);
          setIsLoading(false);
          onError?.(message);
          break;
        case 'sdk':
          // Handle SDK calls
          switch (action) {
            case 'music.play':
              orbitSDK.music.play();
              break;
            case 'music.pause':
              orbitSDK.music.pause();
              break;
            case 'music.toggle':
              orbitSDK.music.toggle();
              break;
            case 'music.next':
              orbitSDK.music.next();
              break;
            case 'music.prev':
              orbitSDK.music.prev();
              break;
            case 'music.playTrack':
              orbitSDK.music.playTrack(data);
              break;
            case 'apps.openMusicPlayer':
              orbitSDK.apps.openMusicPlayer();
              break;
            case 'apps.openPhotoBooth':
              orbitSDK.apps.openPhotoBooth();
              break;
            case 'apps.openGarden':
              orbitSDK.apps.openGarden();
              break;
            case 'apps.openGuestbook':
              orbitSDK.apps.openGuestbook();
              break;
            case 'apps.openOrbitTV':
              orbitSDK.apps.openOrbitTV();
              break;
            case 'apps.openGenius':
              orbitSDK.apps.openGenius();
              break;
            case 'apps.openFileBrowser':
              orbitSDK.apps.openFileBrowser(data);
              break;
            case 'notify':
              orbitSDK.notify(data);
              break;
            case 'storage.set':
              try {
                localStorage.setItem(storagePrefix + data.key, JSON.stringify(data.value));
              } catch (e) {
                console.error('Storage error:', e);
              }
              break;
            case 'storage.clear':
              try {
                Object.keys(localStorage).forEach(key => {
                  if (key.indexOf(storagePrefix) === 0) {
                    localStorage.removeItem(key);
                  }
                });
              } catch (e) {
                console.error('Storage error:', e);
              }
              break;
          }
          break;
      }
    };

    window.addEventListener('message', handleMessage);

    // Timeout for loading
    const timeout = setTimeout(() => {
      if (isLoading) {
        setIsLoading(false);
      }
    }, 5000);

    return () => {
      window.removeEventListener('message', handleMessage);
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, css, orbitSDK, onError]);

  return (
    <div className="terminal-preview">
      {isLoading && (
        <div className="preview-loading">
          <span className="loading-spinner">◐</span> Loading preview...
        </div>
      )}
      {error && (
        <div className="preview-error">
          <div className="error-title">Error</div>
          <div className="error-message">{error}</div>
        </div>
      )}
      <iframe
        ref={iframeRef}
        title={`${appEmoji} ${appName}`}
        className="preview-iframe"
        sandbox={SANDBOX_PERMS}
        style={{ opacity: isLoading ? 0 : 1 }}
      />
    </div>
  );
};

export default TerminalPreview;
